import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Zap, Lock, Check, Loader2 } from "lucide-react";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirmPassword) return toast.error("Passwords don't match");

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, email, password })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        toast.success(data.message);
      } else {
        toast.error(data.detail || "Failed to reset password");
      }
    } catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white flex items-center justify-center p-6">
      <motion.div
        className="w-full max-w-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-crimson flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold">REKRUTOR</span>
        </div>

        {success ? (
          <div className="bg-obsidian-light border border-[#222] p-8 text-center">
            <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Password Reset!</h2>
            <p className="text-gray-400 text-sm mb-6">Your password has been updated. You can now log in.</p>
            <Link to="/">
              <Button className="bg-crimson hover:bg-crimson/80 w-full">Go to Login</Button>
            </Link>
          </div>
        ) : (
          <div className="bg-obsidian-light border border-[#222] p-8">
            <div className="text-center mb-6">
              <Lock className="w-8 h-8 text-crimson mx-auto mb-2" />
              <h2 className="text-xl font-bold">Set New Password</h2>
              <p className="text-gray-500 text-sm mt-1">{email}</p>
            </div>
            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  className="w-full bg-obsidian border border-[#333] px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none"
                  data-testid="new-password"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  className="w-full bg-obsidian border border-[#333] px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none"
                  data-testid="confirm-password"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-crimson hover:bg-crimson/80" data-testid="reset-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Reset Password
              </Button>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPassword;
